import { flow, get, last, noop } from 'lodash';
import update from 'immutability-helper';

import ActionConstants from 'constants/actions';
import surveyEndRedirectPage from 'constants/surveyEndRedirectPage';
import Constants from 'taker/constants';
import { ruleMatchMap } from 'utils/filterAvailableLogicAnswers';
import arrangeBlocksPageNumber from 'utils/arrangeBlocksPageNumber';
import blockConfig from 'sharedParts/config/blockConfig';
import questionTypes from 'sharedParts/config/questionTypes';
import mapEditorSectionToUrl from 'config/mapEditorSectionToUrl';
import questionLogicUtils from 'utils/questionLogicUtils';
import sectionHeadersReducer, * as fromHeader from 'reducers/sectionHeadersReducer';
import { getAnswerById } from './responseReducer';

import { addNumbersToVisitedQuestions, isQuestionBlock } from '../../utils/questionNumberingUtils';

const {
  next_button: { type: nextButtonType },
  submit_button: { type: submitButtonType },
} = blockConfig;

const initialState = {
  blockList: [],
  conditionalShowLogicQuestions: {},
  currentPage: null,
  invalidFormNameToScroll: null,
  isCaptchaPending: true,
  isCaptchaVerified: false,
  isSurveyCompleted: false,
  pendingRedirectToBlockId: null,
  redirectToBlockId: null,
  sectionHeaders: {},
  settings: {},
  sharedHeaderUuid: null,
  visitedPages: [],
  visitedQuestions: [],
};

export default function(state = initialState, action) {
  switch (action.type) {
    case Constants.RESET_CLIENT_SURVEY_DATA: {
      return initialState;
    }

    case Constants.SET_CLIENT_SURVEY_BLOCK_LIST: {
      return update(state, { blockList: { $set: action.payload } });
    }

    case Constants.SET_CLIENT_SURVEY_CONDITIONAL_SHOW_LOGIC_QUESTIONS: {
      return update(state, { conditionalShowLogicQuestions: { $set: action.payload } });
    }

    case Constants.TOGGLE_CONDITIONAL_SHOW_LOGIC_QUESTION_VISIBILITY: {
      const newConditionalShowLogic = [...state.conditionalShowLogicQuestions];
      const questionIndex = newConditionalShowLogic.findIndex((q) => q.questionUuid === action.payload);
      newConditionalShowLogic[questionIndex].visible = !newConditionalShowLogic[questionIndex].visible;
      return update(state, {
        conditionalShowLogicQuestions: {
          $set: newConditionalShowLogic,
        },
      });
    }

    case Constants.SET_INVALID_FORM_NAME_TO_SCROLL: {
      return update(state, { invalidFormNameToScroll: { $set: action.payload } });
    }

    case Constants.POP_FROM_VISITED_PAGES: {
      return update(state, { visitedPages: { $splice: [[-1]] } });
    }

    case Constants.PUSH_TO_VISITED_PAGES: {
      return update(state, { visitedPages: { $push: [action.payload] } });
    }

    case Constants.SET_VISITED_QUESTIONS: {
      return update(state, { visitedQuestions: { $set: action.payload } });
    }

    case Constants.SET_VISITED_PAGES: {
      return update(state, { visitedPages: { $set: action.payload } });
    }

    case Constants.SET_CURRENT_PAGE: {
      return update(state, { currentPage: { $set: action.payload } });
    }

    case Constants.SET_CLIENT_SURVEY_SETTINGS: {
      return update(state, { settings: { $set: action.payload } });
    }

    case Constants.SHOW_ERROR: {
      return update(state, { error: { $set: action.payload } });
    }

    case Constants.CLEAR_INVALID_FORM_NAME_TO_SCROLL: {
      return update(state, { invalidFormNameToScroll: { $set: null } });
    }

    case ActionConstants.SET_SECTION_HEADER: {
      return update(state, {
        sectionHeaders: {
          $set: sectionHeadersReducer(state.sectionHeaders, action),
        },
      });
    }

    case ActionConstants.SET_SECTION_HEADERS: {
      return update(state, {
        sectionHeaders: {
          $set: sectionHeadersReducer(state.sectionHeaders, action),
        },
      });
    }

    case ActionConstants.SET_SHARED_HEADER: {
      return update(state, { sharedHeaderUuid: { $set: action.payload } });
    }

    case Constants.SET_LOGIC_REDIRECT_TO_QUESTION_ID: {
      return update(state, { redirectToBlockId: { $set: action.payload } });
    }

    case Constants.SET_PENDING_LOGIC_REDIRECT_TO_QUESTION_ID: {
      return update(state, { pendingRedirectToBlockId: { $set: action.payload } });
    }

    case Constants.SET_IS_CAPTCHA_PENDING: {
      return update(state, { isCaptchaPending: { $set: action.payload } });
    }

    case Constants.SET_IS_CAPTCHA_VERIFIED: {
      return update(state, { isCaptchaVerified: { $set: action.payload } });
    }

    case ActionConstants.TOGGLE_SURVEY_FULL_WIDTH: {
      return update(state, { settings: { isFullWidth: { $set: action.payload } } });
    }

    case Constants.SET_IS_SURVEY_COMPLETED: {
      return update(state, { isSurveyCompleted: { $set: action.payload } });
    }

    case ActionConstants.SET_VISITED_QUESTION_NUMBERS: {
      return update(state, {
        blockList: {
          $set: addNumbersToVisitedQuestions(state.blockList, state.visitedQuestions),
        },
      });
    }

    default: {
      return state;
    }
  }
}

export const getRawBlockList = (state) => state.blockList;

export const getSurveySectionLastPageNumber = (state) => {
  const paginatedSurveySection = arrangeBlocksPageNumber(
    getRawBlockList(state).filter((block) => block.section === mapEditorSectionToUrl.survey),
  );

  return Math.max(...paginatedSurveySection.map((block) => block.page));
};

export const hasSurveyQuestionsSectionOnlyOnePage = (state) => getSurveySectionLastPageNumber(state) === 0;

export const getBlockList = (state) => arrangeBlocksPageNumber(getRawBlockList(state));

export const getQuestionBlockList = (state) => getBlockList(state).filter(isQuestionBlock);

export const getSettings = (state) => state.settings;

export const getQuestionNumberingEnabled = (state) => getSettings(state).questionNumberingEnabled;

export const getRedirectUrl = (state) => getSettings(state).redirectUrl;

export const isEndEnabled = (state) => getSettings(state).endEnabled;

export const isLandingEnabled = (state) => getSettings(state).landingEnabled;

export const isSurveyFullWidth = (state) => getSettings(state).isFullWidth;

export const isSurveyCompleted = (state) => state.isSurveyCompleted;

export const isCaptchaEnabled = (state) => getSettings(state).reCaptchaEnabled || false;

export const isCaptchaVerified = (state) => state.isCaptchaVerified;

export const isCaptchaPending = (state) => state.isCaptchaPending;

export const shouldRenderCaptcha = (state) => isCaptchaEnabled(state) && !isCaptchaVerified(state);

export const getLastPageNumber = (state) => Math.max(...getBlockList(state).map((block) => block.page));

export const getLatestVisitedPageNumber = (state) =>
  flow(
    get,
    last,
  )(state, 'visitedPages');

export const getVisitedPages = (state) => state.visitedPages;

export const getVisitedQuestions = (state) => state.visitedQuestions;

export const getVisitedPagesLength = (state) => get(getVisitedPages(state), 'length');

export const getCurrentPage = (state) => state.currentPage;

export const isFirstPage = (state) => getCurrentPage(state) === 0;

export const isLastPage = (state) => getLastPageNumber(state) === getCurrentPage(state);

export const shouldRenderPoweredBy = (state) =>
  isSurveyCompleted(state) || (!shouldRenderCaptcha(state) && isLastPage(state));

export const getDisplayPage = (state) => {
  const displayPageOffset = isLandingEnabled(state) ? 1 : 0;
  return getVisitedPagesLength(state) - displayPageOffset;
};

export const getRedirectToBlockId = (state) => state.redirectToBlockId;

export const getPendingRedirectToBlockId = (state) => state.pendingRedirectToBlockId;

export const getFirstPageNumber = (state) => Math.min(...getBlockList(state).map((block) => block.page));

export const getPageBlockList = (state, page) => {
  return getBlockList(state)
    .filter((block) => block.page === page)
    .sort((a, b) => a.order - b.order);
};

export const getRedirectedPageBlockList = (state, page) => {
  const pageBlockList = getPageBlockList(state, page);
  const redirectToBlockId = getRedirectToBlockId(state);
  if (redirectToBlockId) {
    const redirectToBlockIdPageIndex = pageBlockList.findIndex((block) => block.id === redirectToBlockId);
    const isRedirectToBlockOnCurrentPage = redirectToBlockIdPageIndex !== -1;
    if (isRedirectToBlockOnCurrentPage) {
      return pageBlockList.slice(redirectToBlockIdPageIndex);
    }
  }

  return pageBlockList;
};

export const getCurrentPageBlockList = (state) => getRedirectedPageBlockList(state, getCurrentPage(state));

export const getError = (state) => state.error;

export const getBlockById = (state, id) => getBlockList(state).find((item) => item.id === id) || null;

export const getBlockByUuid = (state, clientUuid) =>
  getBlockList(state).find((item) => item.clientUuid === clientUuid) || {};

export const getBlockIdByUuid = (state, clientUuid) =>
  get(getBlockList(state).find((item) => item.clientUuid === clientUuid), 'id', null);

export const getBlockType = (state, id) => get(getBlockById(state, id), 'type', null);

export const getBlockPage = (state, id) => get(getBlockById(state, id), 'page', null);

export const isBlockNextButton = (state, id) => getBlockType(state, id) === nextButtonType;

export const isBlockNextOrSubmitButton = (state, id) =>
  [submitButtonType, nextButtonType].includes(getBlockType(state, id));

export const getLastQuestionIdOnPage = (state, page = getCurrentPage(state)) =>
  get(
    getRedirectedPageBlockList(state, page)
      .reverse()
      .find((block) => !isBlockNextOrSubmitButton(state, block.id)),
    'id',
  );

export const getHeaderEndEnabled = (state) => getSettings(state).headerEndEnabled;

export const getHeaderLandingEnabled = (state) => getSettings(state).headerLandingEnabled;

export const getHeaderQuestionsEnabled = (state) => getSettings(state).headerQuestionsEnabled;

export const getHeaderEnabled = (state, sectionName) => {
  const { landing: sectionLanding, survey: sectionQuestions, ty: sectionEnd } = mapEditorSectionToUrl;

  return ({
    [sectionEnd]: getHeaderEndEnabled,
    [sectionLanding]: getHeaderLandingEnabled,
    [sectionQuestions]: getHeaderQuestionsEnabled,
  }[sectionName] || noop)(state);
};

// skip logic
export const isQuestionLogicValid = (state, questionId) =>
  questionLogicUtils.isQuestionLogicValid(getBlockById(state, questionId));

export const isQuestionLogicActionShow = (state, questionId) =>
  questionLogicUtils.isQuestionLogicShow(getBlockById(state, questionId));

export const isQuestionLogicActionSkip = (state, questionId) =>
  questionLogicUtils.isQuestionLogicSkip(getBlockById(state, questionId));

export const getQuestionLogicRedirectBlockUuid = (state, questionId, answer) =>
  questionLogicUtils.getQuestionLogicRedirectBlockUuid(getBlockById(state, questionId), answer);

export const getQuestionLogicRedirectBlockId = (state, questionId, answer) => {
  const redirectBlockUuid = getQuestionLogicRedirectBlockUuid(state, questionId, answer);

  return redirectBlockUuid === questionLogicUtils.thankYouPageValue
    ? questionLogicUtils.thankYouPageValue
    : getBlockIdByUuid(state, redirectBlockUuid);
};

export const getQuestionLogicRedirectPage = (state, questionId, answer) => {
  const redirectBlockId = getQuestionLogicRedirectBlockId(state, questionId, answer);

  if (redirectBlockId === questionLogicUtils.thankYouPageValue) {
    const lastPageNumber = getLastPageNumber(state);
    return !isEndEnabled(state) ? surveyEndRedirectPage : lastPageNumber;
  }

  return getBlockPage(state, redirectBlockId);
};
// end skip logic

// Show Logic
export const getConditionalShowLogicQuestions = (state) => state.conditionalShowLogicQuestions;

export const getConditionalShowLogicQuestionByUuid = (state, questionUuid) =>
  getConditionalShowLogicQuestions(state).find((question) => question.questionUuid === questionUuid);

export const isConditionalShowLogicQuestion = (state, questionUuid) =>
  getConditionalShowLogicQuestions(state).some((question) => question.questionUuid === questionUuid);

const validateAnswerValueMatch = ({
  controlQuestionAnswer,
  controlQuestionType,
  logicTriggerValue,
  ruleFn,
}) => {
  const controlQuestionAnswerValue = [questionTypes.questionScale, questionTypes.questionNumeric].includes(
    controlQuestionType,
  )
    ? controlQuestionAnswer.value
    : controlQuestionAnswer.id;
  if (Array.isArray(logicTriggerValue)) {
    return logicTriggerValue.some((value) => ruleFn(controlQuestionAnswerValue, value));
  }
  return ruleFn(controlQuestionAnswerValue, logicTriggerValue);
};

export const answersMatchShowLogicConditions = (surveyState, answerState, questionUuid) => {
  const conditionalQuestionInfo = getConditionalShowLogicQuestionByUuid(surveyState, questionUuid);

  if (!!conditionalQuestionInfo.triggerConditions) {
    return conditionalQuestionInfo.triggerConditions.some(
      ({ controlQuestionId, answerTriggerOption, logicTriggerValue }) => {
        const ruleFn = ruleMatchMap[answerTriggerOption];
        const controlQuestionAnswer = getAnswerById(answerState, controlQuestionId);
        const controlQuestionType = getBlockType(surveyState, controlQuestionId);
        if (!!controlQuestionAnswer && !!(controlQuestionAnswer.value || controlQuestionAnswer.id)) {
          if (Array.isArray(controlQuestionAnswer.value)) {
            return controlQuestionAnswer.value.some((answer) =>
              validateAnswerValueMatch({
                controlQuestionAnswer: answer,
                controlQuestionType,
                logicTriggerValue,
                ruleFn,
              }),
            );
          }
          return validateAnswerValueMatch({
            controlQuestionAnswer,
            controlQuestionType,
            logicTriggerValue,
            ruleFn,
          });
        }
        return false;
      },
    );
  }
  return false;
};

export const shouldShowQuestionBlock = (surveyState, answerState, questionUuid) =>
  !isConditionalShowLogicQuestion(surveyState, questionUuid) ||
  (isConditionalShowLogicQuestion(surveyState, questionUuid) &&
    answersMatchShowLogicConditions(surveyState, answerState, questionUuid));

export const getConditionalShowLogicQuestionVisibility = (state, questionUuid) =>
  getConditionalShowLogicQuestionByUuid(state, questionUuid).visible;

/**
 * Filter the blocklist to display the normal questions together with the conditional show logic questions
 *  if the user answer matches the conditions to do so.
 */
export const getVisibleQuestionBlocks = (surveyState, answerState, blockList = []) =>
  blockList.filter(({ clientUuid }) => shouldShowQuestionBlock(surveyState, answerState, clientUuid));
// End Show Logic

export const getInvalidFormNameToScroll = (state) => state.invalidFormNameToScroll;

export const getEditorSharedHeader = (state) =>
  fromHeader.getHeaderByUuid(state.sectionHeaders, state.sharedHeaderUuid);

export const getSharedHeaderSection = (state) => get(getEditorSharedHeader(state), 'sectionAssignee');

export const getSectionHeaderBackgroundImageUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundImageUrl(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageUrl(state.sectionHeaders, sectionName);

export const getSectionHeaderBackgroundImageSize = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundImageSize(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageAlign = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageAlign(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoWithTextAlign = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoWithTextAlign(state.sectionHeaders, sectionName);

export const getSectionHeaderHeight = (state, sectionName) =>
  fromHeader.getSectionHeaderHeight(state.sectionHeaders, sectionName);

export const getSectionHeaderBackgroundColor = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundColor(state.sectionHeaders, sectionName);

export const isSectionHeaderFullWidth = (state, sectionName) =>
  fromHeader.isSectionHeaderFullWidth(state.sectionHeaders, sectionName) || isSurveyFullWidth(state);

export const getSectionHeaderBackgroundCoords = (state, sectionName) =>
  fromHeader.getSectionHeaderBackgroundCoords(state.sectionHeaders, sectionName);

export const getHeaderPageBoxType = (state, sectionName) =>
  fromHeader.getHeaderPageBoxType(state.sectionHeaders, sectionName, isSurveyFullWidth(state));

export const isSectionHeaderPlaceholder = (state, sectionName) =>
  fromHeader.isSectionHeaderPlaceholder(state.sectionHeaders, sectionName);

export const isSectionHeaderContentEnabled = (state, sectionName) =>
  fromHeader.isSectionHeaderContentEnabled(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageLinkUrl = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageLinkUrl(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageSizePreset = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageSizePreset(state.sectionHeaders, sectionName);

export const getSectionHeaderLogoImageSize = (state, sectionName) =>
  fromHeader.getSectionHeaderLogoImageSize(state.sectionHeaders, sectionName);

export const getSectionHeaderLayout = (state, sectionName) =>
  fromHeader.getSectionHeaderLayout(state.sectionHeaders, sectionName);

export const getSectionHeaderTextContent = (state, sectionName) =>
  fromHeader.getSectionHeaderTextContent(state.sectionHeaders, sectionName);

export const getSectionHeaderClientUuid = (state, sectionName) =>
  fromHeader.getSectionHeaderClientUuid(state.sectionHeaders, sectionName);

export const isBackButtonEnabled = (state) => getSettings(state).isBackButtonEnabled;
